import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import { formatDuration, formatStorageBytes } from "./formatDuration";
import { formatPrice, detectCurrency } from "./currencyUtils";
import FaqItem from "./components/FaqItem";
import { initLandingAnimations } from "./landingAnimations";
import "./LandingPage.css";

// Helper: get CMS value with fallback (same contract as the landing page,
// so admin edits to the pricing/faq/cta sections apply here too).
const cms = (content, section, key, fallback) =>
  (content && content[section] && content[section][key]) || fallback;

/* Dedicated pricing page (hiprup-style): the full plan grid lives here;
   the home page links to /pricing instead of scrolling to a section. */
const PricingPage = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("lp-theme") !== "light");
  const [cmsContent, setCmsContent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [storageTiers, setStorageTiers] = useState([]);
  const [selectedTierSlug, setSelectedTierSlug] = useState("500gb");
  // Currency is auto-detected from the visitor's region (saved choices from
  // the billing pages still win) — no dropdown on the public pricing page.
  const [currency] = useState(() => detectCurrency());
  const [exchangeRates, setExchangeRates] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/exchange-rates`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setExchangeRates(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/landing-content`)
      .then((r) => r.json())
      .then((data) => setCmsContent(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/subscription-plans`)
      .then((r) => r.json())
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/storage-tiers`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setStorageTiers(data);
          setSelectedTierSlug(data[0].slug);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenu ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenu]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMobileMenu(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useLayoutEffect(() => {
    animRef.current = initLandingAnimations(rootRef.current);
    return () => animRef.current?.cleanup();
  }, []);

  useEffect(() => {
    animRef.current?.refresh();
  }, [cmsContent, plans, storageTiers]);

  const toggleTheme = () => {
    setDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem("lp-theme", next ? "dark" : "light");
      return next;
    });
  };

  const choosePlan = (slug) => {
    const token = localStorage.getItem("token");
    navigate(token ? `/subscribe/${slug}` : `/login?plan=${slug}`);
  };

  const planFeatures = (plan) => {
    const activeTier = plan.slug === "pro_max_ultra" && storageTiers.length > 0
      ? (storageTiers.find((t) => t.slug === selectedTierSlug) || storageTiers[0])
      : null;
    const storageLine = activeTier
      ? `${activeTier.name} storage · original quality`
      : plan.max_storage_bytes
        ? `${formatStorageBytes(plan.max_storage_bytes)} storage`
        : null;
    const compressionLine = plan.slug === "pro_max_ultra"
      ? null
      : plan.no_compression
        ? "Original quality — zero compression"
        : plan.max_uncompressed_events > 0
          ? `${plan.max_uncompressed_events} original-quality events/mo`
          : "Smart compression";
    return [
      ...(formatDuration(plan.duration_days) ? [`Valid for ${formatDuration(plan.duration_days)}`] : []),
      plan.max_events === null ? "Unlimited events" : `${plan.max_events} events`,
      plan.max_photos_per_event === null ? "Unlimited photos per event" : `${plan.max_photos_per_event} photos per event`,
      ...(storageLine ? [storageLine] : []),
      ...(compressionLine ? [compressionLine] : []),
      "AI face recognition",
      "Client gallery, QR & live slideshow",
      "Contracts, invoices & PDF proofing",
      "Portfolio page & studio website",
      ...(["pro", "pro_max_ultra"].includes(plan.slug)
        ? ["CRM — leads, branded quotes & follow-ups"] : []),
      ...(plan.slug === "pro_max_ultra" ? ["RAW camera file upload & backup", "Photentra AI · unlimited team members", "Priority support"] : []),
    ];
  };

  const planTagline = (slug) => ({
    starter: "Perfect for solo photographers",
    pro: "For growing photography businesses",
    pro_plus: "For studios & agencies at scale",
    pro_max_ultra: "Unlimited events & photos. Choose your storage.",
  }[slug] || "Everything you need to deliver beautifully");

  const visiblePlans = plans.filter((p) => p.slug !== "free" && p.slug !== "free_trial");

  // Home sections reachable from this page's nav — link back with a hash.
  const navItems = [
    ["features", "Features"],
    ["album-review", "Album Review"],
    ["services", "Services"],
    ["testimonials", "Stories"],
    ["faq", "FAQ"],
  ];
  const goHome = (id) => {
    setMobileMenu(false);
    navigate(id ? `/#${id}` : "/");
  };

  // Pricing-relevant questions, reusing the landing FAQ's CMS keys so admin
  // edits flow to both pages.
  const pricingFaqs = [
    { i: 2, q: "Is there a limit on photo uploads?", a: "Starter supports up to 1,000 photos per event. Pro supports up to 3,000 photos per event. Pro Max supports up to 2,500 photos per event at original quality. Pro Max Ultra offers completely unlimited uploads with 1 TB of storage." },
    { i: 6, q: "Can I cancel my subscription anytime?", a: "Yes, you can cancel anytime with no questions asked. Your data remains accessible for 30 days after cancellation." },
    { i: 5, q: "Is my data secure?", a: "Absolutely. All photos are stored on AWS S3 with encryption. We use HTTPS everywhere, and your data is never shared with third parties." },
  ];

  return (
    <div className={`lp${darkMode ? " lp--dark" : ""}`} ref={rootRef}>
      <div className="lp-gridbg" aria-hidden="true" />
      <div className="lp-grain" aria-hidden="true" />
      <span className="lp-progress" aria-hidden="true" />

      {/* ────────── HEADER ────────── */}
      <header className={`lp-header${scrolled ? " is-scrolled" : ""}`}>
        <div className="lp-header__inner">
          <button className="lp-logo" onClick={() => goHome()} aria-label="Back to home">
            <img src={process.env.PUBLIC_URL + "/photentra-logo.png"} alt="Photentra" className="lp-logo__img" />
          </button>

          <nav className="lp-nav" aria-label="Primary">
            {navItems.map(([id, label]) => (
              <button key={id} className="lp-nav__link" onClick={() => goHome(id)}>
                {label}
              </button>
            ))}
            <button className="lp-nav__link lp-nav__link--active" aria-current="page">
              Pricing
            </button>
          </nav>

          <div className="lp-header__actions">
            <button className="lp-icon-btn" onClick={toggleTheme} aria-label="Toggle colour theme">
              {darkMode ? (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6"/><path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
              )}
            </button>
            <button className="lp-btn lp-btn--ghost lp-hide-sm" onClick={() => navigate("/login")}>Log In</button>
            <button className="lp-btn lp-btn--primary" onClick={() => navigate("/signup")}>Get Started</button>
          </div>

          <button
            className={`lp-hamburger${mobileMenu ? " is-open" : ""}`}
            onClick={() => setMobileMenu(!mobileMenu)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenu}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ────────── MOBILE DRAWER ────────── */}
      <div
        className={`lp-drawer-backdrop${mobileMenu ? " is-open" : ""}`}
        onClick={() => setMobileMenu(false)}
        aria-hidden="true"
      />
      <aside className={`lp-drawer${mobileMenu ? " is-open" : ""}`} aria-hidden={!mobileMenu}>
        <nav className="lp-drawer__nav">
          {navItems.map(([id, label], i) => (
            <button
              key={id}
              className="lp-drawer__link"
              style={{ transitionDelay: `${mobileMenu ? 60 + i * 40 : 0}ms` }}
              onClick={() => goHome(id)}
            >
              <span className="lp-drawer__idx">0{i + 1}</span>
              {label}
            </button>
          ))}
        </nav>
        <div className="lp-drawer__actions">
          <button className="lp-btn lp-btn--outline lp-btn--block" onClick={() => { setMobileMenu(false); navigate("/login"); }}>
            Log In
          </button>
          <button className="lp-btn lp-btn--primary lp-btn--block" onClick={() => { setMobileMenu(false); navigate("/signup"); }}>
            Get Started
          </button>
        </div>
      </aside>

      {/* ────────── PRICING HERO ────────── */}
      <section className="lp-section lp-pricing-page" id="pricing">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "pricing", "section_tag", "Pricing")}</span>
          <h1 className="lp-shead__title">
            {cms(cmsContent, "pricing", "section_title", "Simple, Transparent Pricing")}
          </h1>
          <p className="lp-shead__desc">
            {cms(cmsContent, "pricing", "section_desc", "Start free and scale as you grow. No hidden fees, cancel anytime.")}
          </p>
          {currency !== "INR" && (
            <p className="lp-mono lp-currency-note">
              Prices shown in {currency}, based on your region
            </p>
          )}
        </header>

        <div className={`lp-pricing lp-pricing--${Math.min(visiblePlans.length, 4) || 3}`}>
          {visiblePlans.map((plan) => {
            const popular = plan.slug === "pro";
            const premium = plan.slug === "pro_max_ultra";
            return (
              <article
                key={plan.slug}
                className={`lp-plan${popular ? " lp-plan--popular" : ""}${premium ? " lp-plan--premium" : ""}`}
                data-reveal
              >
                {popular && <span className="lp-plan__badge">Most Popular</span>}
                {premium && <span className="lp-plan__badge lp-plan__badge--premium">Ultimate</span>}
                <h2 className="lp-plan__name">{plan.name}</h2>
                <p className="lp-plan__tagline">{planTagline(plan.slug)}</p>
                {premium && storageTiers.length > 0 ? (() => {
                  const activeTier = storageTiers.find((t) => t.slug === selectedTierSlug) || storageTiers[0];
                  return (
                    <>
                      <div className="lp-plan__tiers">
                        {storageTiers.map((t) => (
                          <button
                            key={t.slug}
                            className={`lp-tier-pill${selectedTierSlug === t.slug ? " lp-tier-pill--active" : ""}`}
                            onClick={() => setSelectedTierSlug(t.slug)}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                      <div className="lp-plan__price" style={{ marginTop: 14 }}>
                        <span className="lp-plan__amt">{formatPrice(activeTier.price_monthly, currency, exchangeRates)}</span>
                      </div>
                      <p style={{ fontSize: "0.78rem", margin: "2px 0 4px", opacity: 0.65 }}>/month · {formatPrice(activeTier.price_yearly, currency, exchangeRates)} billed yearly</p>
                    </>
                  );
                })() : (
                  <div className="lp-plan__price">
                    <span className="lp-plan__amt">{formatPrice(plan.price, currency, exchangeRates)}</span>
                  </div>
                )}
                <span className="lp-plan__rule" aria-hidden="true" />
                <ul className="lp-plan__list">
                  {planFeatures(plan).map((f) => (
                    <li key={f}>
                      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  className={`lp-btn ${popular || premium ? "lp-btn--primary" : "lp-btn--outline"} lp-btn--block`}
                  onClick={() => choosePlan(plan.slug)}
                >
                  {premium ? "Get Pro Max" : `Choose ${plan.name}`}
                </button>
              </article>
            );
          })}
        </div>

        <p className="lp-pricing-note" data-reveal>
          {cms(cmsContent, "pricing", "note", "Every plan includes AI face recognition, client galleries, and a free studio website. Prices shown per month.")}
        </p>
      </section>

      {/* ────────── PRICING FAQ ────────── */}
      <section className="lp-section lp-section--alt">
        <header className="lp-shead" data-reveal>
          <span className="lp-tag">{cms(cmsContent, "faq", "section_tag", "FAQ")}</span>
          <h2 className="lp-shead__title">Questions About Plans</h2>
        </header>
        <div className="lp-faq">
          {pricingFaqs.map(({ i, q, a }) => (
            <FaqItem
              key={i}
              question={cms(cmsContent, "faq", `question_${i}`, q)}
              answer={cms(cmsContent, "faq", `answer_${i}`, a)}
            />
          ))}
        </div>
      </section>

      {/* ────────── CTA ────────── */}
      <section className="lp-cta">
        <div className="lp-cta__aura" aria-hidden="true" />
        <div className="lp-cta__inner">
          <h2 className="lp-cta__title">
            {cms(cmsContent, "cta", "title", "Ready to Transform Your Photography Business?")}
          </h2>
          <p className="lp-cta__desc">
            {cms(cmsContent, "cta", "description", "Join thousands of photographers who deliver faster, impress clients, and grow their studios with Photentra.")}
          </p>
          <button className="lp-btn lp-btn--white lp-btn--lg" data-magnetic onClick={() => navigate("/signup")}>
            {cms(cmsContent, "cta", "button_text", "Get Started")}
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h11M11 5.5 15.5 10 11 14.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <p className="lp-cta__note">
            {cms(cmsContent, "cta", "note", "No credit card required · Free forever on Starter plan")}
          </p>
        </div>
      </section>

      {/* ────────── FOOTER ────────── */}
      <footer className="lp-footer">
        <div className="lp-footer__bottom">
          <p>&copy; {new Date().getFullYear()} Photentra AI Technologies PVT LTD. All rights reserved.</p>
          <div className="lp-footer__links">
            <button onClick={() => goHome()}>Home</button>
            <button onClick={() => navigate("/privacy")}>Privacy Policy</button>
            <button onClick={() => navigate("/terms")}>Terms of Service</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
